/**
 * Marketing analytics (PostHog).
 *
 * Loaded lazily via dynamic import so the prerender script (Node/tsx) never
 * touches posthog-js, and the main bundle stays lean — the SDK code-splits
 * into its own chunk. Disabled entirely when REACT_APP_POSTHOG_KEY is unset
 * (local dev default).
 *
 * `cookieless_mode: 'always'` keeps the marketing site GDPR-clean without a
 * cookie banner: no cookies/localStorage persistence, events are anonymous.
 */

type EventProperties = Record<string, unknown>;

type PostHogClient = typeof import('posthog-js').default;

let client: PostHogClient | null = null;
let started = false;
const queue: Array<[string, EventProperties | undefined]> = [];

export function initAnalytics(): void {
  if (started || typeof window === 'undefined') return;
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key) return;
  started = true;
  void import('posthog-js')
    .then((mod) => {
      client = mod.default;
      client.init(key, {
        api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
        person_profiles: 'identified_only',
        cookieless_mode: 'always',
        autocapture: false,
        capture_pageview: true,
        capture_pageleave: true,
      });
      for (const [event, properties] of queue) client.capture(event, properties);
      queue.length = 0;
    })
    .catch((err) => {
      started = false;
      console.warn('[analytics] posthog-js failed to load:', err);
    });
}

export function trackEvent(event: string, properties?: EventProperties): void {
  if (typeof window === 'undefined') return;
  if (client) {
    client.capture(event, properties);
    return;
  }
  if (started) queue.push([event, properties]);
}
